import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import './TrackerPage.css';
import { getStoredAccessToken, SESSION_EXPIRED_MESSAGE } from '../../auth/utils/authToken';

// Same backend path pathbuddy.app uses for working Spaces + DB uploads.
// Prefer REACT_APP_API_URL (lobster on pathbuddy.com) when the build sets it.
const TRACKER_API_FALLBACK =
  'https://hammerhead-app-8cr38.ondigitalocean.app/website-backend';

const TrackerPage = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tracker, setTracker] = useState(null);
  const [error, setError] = useState('');
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [editingCommentId, setEditingCommentId] = useState('');

  // Staff of an active organisation (the server's own answer in
  // user.access). The tracker's staff routes require the same membership.
  const isTeacher = user?.access?.org?.role === 'staff';

  const backendUrl = process.env.REACT_APP_API_URL || TRACKER_API_FALLBACK;

  const handleSessionExpired = () => {
    setError(SESSION_EXPIRED_MESSAGE);
    setTimeout(() => navigate('/login'), 2000);
  };

  const handleAuthFailure = (status) => {
    if (status === 401) {
      handleSessionExpired();
      return true;
    }
    return false;
  };

  const loadTracker = async (studentId = null) => {
    try {
      setLoading(true);
      setError('');

      const token = getStoredAccessToken();

      if (!token) {
        handleSessionExpired();
        setLoading(false);
        return;
      }

      const apiUrl = isTeacher && studentId
        ? `${backendUrl}/api/tracker/student/${studentId}`
        : `${backendUrl}/api/tracker/me`;
      const res = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const text = await res.text();
        // eslint-disable-next-line no-console
        console.error('Tracker load error', res.status, text);
        if (handleAuthFailure(res.status)) {
          setLoading(false);
          return;
        }
        setError('Failed to load tracker.');
        setLoading(false);
        return;
      }

      const data = await res.json();
      setTracker(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Tracker load exception', e);
      setError('Network error loading tracker.');
    } finally {
      setLoading(false);
    }
  };

  const loadTeacherStudents = async () => {
    try {
      const token = getStoredAccessToken();
      if (!token) {
        handleSessionExpired();
        setLoading(false);
        return;
      }
      const apiUrl = `${backendUrl}/api/tracker/students`;
      const res = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        if (handleAuthFailure(res.status)) {
          setLoading(false);
          return;
        }
        setLoading(false);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data.students) ? data.students : [];
      setStudents(list);
      if (list.length > 0) {
        const fromQuery = searchParams.get('studentId');
        const hasQuery = fromQuery && list.some((s) => s.id === fromQuery);
        const initialStudentId = hasQuery ? fromQuery : list[0].id;
        setSelectedStudentId(initialStudentId);
        await loadTracker(initialStudentId);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error('Error loading teacher students', e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isTeacher) {
      loadTeacherStudents();
    } else {
      loadTracker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  const handleAddOrEditComment = async () => {
    if (!isTeacher || !selectedStudentId || !commentDraft.trim()) return;
    try {
      setSaving(true);
      const token = getStoredAccessToken();
      if (!token) {
        handleSessionExpired();
        return;
      }
      const url = editingCommentId
        ? `${backendUrl}/api/tracker/comments/${editingCommentId}`
        : `${backendUrl}/api/tracker/student/${selectedStudentId}/comments`;
      const method = editingCommentId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ comment_text: commentDraft }),
      });
      if (!res.ok) {
        if (handleAuthFailure(res.status)) return;
        setError('Failed to save comment.');
        return;
      }
      setCommentDraft('');
      setEditingCommentId('');
      await loadTracker(selectedStudentId);
    } catch (e) {
      console.error('Comment save error', e);
      setError('Failed to save comment.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (documentType, file) => {
    try {
      setSaving(true);
      setError('');

      const token = getStoredAccessToken();

      if (!token) {
        handleSessionExpired();
        return;
      }

      const apiUrl = `${backendUrl}/api/tracker/documents/upload`;
      const formData = new FormData();
      formData.append('document_type', documentType);
      formData.append('file', file);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        // eslint-disable-next-line no-console
        console.error('Tracker document save error', res.status, text);
        if (handleAuthFailure(res.status)) return;
        let message = 'Failed to upload document.';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } catch {
          /* keep default */
        }
        setError(message);
        return;
      }

      await loadTracker(isTeacher ? selectedStudentId : null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Tracker document save exception', e);
      setError('Network error saving document.');
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      {error && <div className="tracker-error">{error}</div>}

      {loading ? (
        <div className="tracker-loading">
          <div className="loading-spinner" />
          <p>Loading your tracker...</p>
        </div>
      ) : (
        <div className="tracker-layout">
          {isTeacher && (
            <section className="tracker-comments-section">
              <h2>Students in your groups</h2>
              {students.length === 0 ? (
                <p className="tracker-empty-comments">No students found in your groups yet.</p>
              ) : (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Select student</label>
                    <select
                      value={selectedStudentId}
                      onChange={async (e) => {
                        const value = e.target.value;
                        setSelectedStudentId(value);
                        await loadTracker(value);
                      }}
                    >
                      {students.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.email})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </section>
          )}

          <section className="tracker-documents-section" data-tour="tracker-documents">
            <h2>{isTeacher ? 'Student documents' : 'Your documents'}</h2>
            <div className="tracker-documents-grid">
              <TrackerDocumentCard
                label="CV / Résumé"
                document={tracker?.documents?.cv || null}
                saving={saving}
                onUpload={(file) => handleFileUpload('cv', file)}
                setError={setError}
                readOnly={isTeacher}
              />
              <TrackerDocumentCard
                label="Cover Letter"
                document={tracker?.documents?.cover_letter || null}
                saving={saving}
                onUpload={(file) => handleFileUpload('cover_letter', file)}
                setError={setError}
                readOnly={isTeacher}
              />
              <TrackerDocumentCard
                label="Personal Statement"
                document={tracker?.documents?.personal_statement || null}
                saving={saving}
                onUpload={(file) => handleFileUpload('personal_statement', file)}
                setError={setError}
                readOnly={isTeacher}
              />
            </div>
          </section>

          <section className="tracker-comments-section">
            <h2>{isTeacher ? 'Comment timeline' : 'Instructor comments'}</h2>
            {isTeacher && selectedStudentId && (
              <div className="tracker-upload-inline" style={{ marginBottom: '12px' }}>
                <textarea
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Write feedback for this student..."
                  rows={3}
                />
                <button
                  type="button"
                  className="tracker-upload-button"
                  disabled={saving}
                  onClick={handleAddOrEditComment}
                >
                  {saving ? 'Saving...' : (editingCommentId ? 'Update comment' : 'Add comment')}
                </button>
              </div>
            )}
            {tracker?.comments && tracker.comments.length > 0 ? (
              <ul className="tracker-comments-list">
                {tracker.comments.map((c) => (
                  <li key={c.id} className="tracker-comment-item">
                    <div className="tracker-comment-meta">
                      <span className="tracker-comment-author">Instructor</span>
                      <span className="tracker-comment-date">
                        {c.created_at
                          ? new Date(c.created_at).toLocaleString('en-GB')
                          : ''}
                      </span>
                    </div>
                    <p className="tracker-comment-text">{c.comment_text}</p>
                    {isTeacher && String(c.teacher_user_id) === String(user?.id) && (
                      <button
                        type="button"
                        className="tracker-link"
                        onClick={() => {
                          setEditingCommentId(c.id);
                          setCommentDraft(c.comment_text || '');
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="tracker-empty-comments">
                No comments yet. Once an instructor has reviewed your documents, feedback
                will appear here.
              </p>
            )}
          </section>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="profile-section tracker-embedded" data-tour="profile-tracker">
        <div className="section-header">
          <div className="section-header-info">
            <h2>Tracker</h2>
            <p>Upload your CV, cover letter and personal statement. Files sync to storage and your account; instructor feedback appears below.</p>
          </div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className="tracker-page">
      <header className="tracker-standalone-header">
        <h1>Tracker</h1>
      </header>
      <main className="tracker-main">
        <div className="tracker-container">
          <p className="tracker-standalone-lead">
            Keep your CV, cover letter and personal statement in one place, and track feedback over time.
          </p>
          {body}
        </div>
      </main>
    </div>
  );
};

const TrackerDocumentCard = ({ label, document, saving, onUpload, setError, readOnly = false }) => {
  const inputRef = useRef(null);

  const onFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const name = (file.name || '').toLowerCase();
    const allowedExt =
      name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx');
    // Some browsers (esp. Windows) leave file.type empty for Word docs.
    if (!allowedTypes.includes(file.type) && !allowedExt) {
      setError('Please upload a PDF or Word document.');
      return;
    }
    onUpload(file);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="tracker-card">
      <div className="tracker-card-header">
        <h3>{label}</h3>
        {document && (
          <span className="tracker-doc-filename">{document.original_filename}</span>
        )}
      </div>
      <div className="tracker-card-body">
        {document ? (
          <div className="tracker-current-file">
            <span className="tracker-doc-label">Current file</span>
            <a
              href={document.spaces_url}
              target="_blank"
              rel="noopener noreferrer"
              className="tracker-link"
            >
              View
            </a>
          </div>
        ) : (
          <p className="tracker-doc-empty">No file uploaded yet.</p>
        )}
        {!readOnly && (
          <>
            <div className="tracker-upload-inline">
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onFileChange}
              />
              <button
                type="button"
                className="tracker-upload-button"
                disabled={saving}
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                {saving ? 'Uploading...' : 'Upload'}
              </button>
            </div>
            <small className="tracker-hint">
              Upload a PDF or Word document. Files are stored securely and can be updated later.
            </small>
          </>
        )}
      </div>
    </div>
  );
};

export default TrackerPage;
